import React from "react";
import { Helmet } from "react-helmet-async";

export type SEOProps = {
  title: string;
  description: string;
  path?: string; // e.g. "/blog"
  image?: string; // absolute or relative
  type?: "website" | "article" | "product" | string;
  noindex?: boolean;
  structuredData?: Record<string, any> | Record<string, any>[]; // JSON-LD
  keywords?: string; // SEO keywords
  author?: string;
  language?: string; // e.g. "en-IN"
  geo?: { region?: string; placename?: string }; // Geographic targeting
};

const siteName = "Careerfy";
const defaultImage = "https://www.careerfy.ai/lovable-uploads/135b3376-b684-4651-8f25-1bee28e6a6d7.png"; // global default OG/Twitter image

export function SEO({ title, description, path = "", image = defaultImage, type = "website", noindex = false, structuredData, keywords, author, language = "en-IN", geo }: SEOProps) {
  // Always use canonical domain with www to prevent duplicate indexing
  const canonicalDomain = "https://www.careerfy.ai";
  const canonical = path ? `${canonicalDomain}${path.startsWith('/') ? path : `/${path}`}` : canonicalDomain;
  const imgUrl = image.startsWith("http") ? image : `${canonicalDomain}${image}`;

  const jsonLd = Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      {language && <meta httpEquiv="content-language" content={language} />}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index,follow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Geographic targeting */}
      {geo?.region && <meta name="geo.region" content={geo.region} />}
      {geo?.placename && <meta name="geo.placename" content={geo.placename} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={siteName} />
      {imgUrl && <meta property="og:image" content={imgUrl} />}
      {imgUrl && <meta property="og:image:alt" content={title} />}
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imgUrl && <meta name="twitter:image" content={imgUrl} />}
      {imgUrl && <meta name="twitter:image:alt" content={title} />}

      {/* JSON-LD */}
      {jsonLd.map((data, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
}
